import { useState, useEffect } from 'react'

/**
 * useMediaQuery - React hook for responsive media queries
 *
 * @param query - CSS media query string
 * @returns boolean - true if media query matches
 *
 * @example
 * ```tsx
 * const isMobile = useMediaQuery('(max-width: 768px)')
 * const isDesktop = useMediaQuery('(min-width: 1024px)')
 * ```
 */
export function useMediaQuery(query: string): boolean {
	const [matches, setMatches] = useState<boolean>(false)

	useEffect(() => {
		// Check if window is defined (for SSR compatibility)
		if (typeof window === 'undefined') {
			return
		}

		const mediaQuery = window.matchMedia(query)
		setMatches(mediaQuery.matches)

		const handler = (event: MediaQueryListEvent) => {
			setMatches(event.matches)
		}

		// Modern browsers
		if (mediaQuery.addEventListener) {
			mediaQuery.addEventListener('change', handler)
		} else {
			// Fallback for older browsers
			mediaQuery.addListener(handler)
		}

		return () => {
			if (mediaQuery.removeEventListener) {
				mediaQuery.removeEventListener('change', handler)
			} else {
				mediaQuery.removeListener(handler)
			}
		}
	}, [query])

	return matches
}
